import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PestControlIcon from '@mui/icons-material/PestControl';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import FlightIcon from '@mui/icons-material/Flight';
import SensorsIcon from '@mui/icons-material/Sensors';
import RouterIcon from '@mui/icons-material/Router';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';

export interface TreeDevice {
  id: string;
  name: string;
  parentId: string | null;
  isActive: boolean;
  deviceType?: {
    name: string;
    deviceCategory?: string | null;
  };
}

interface DeviceTreeProps {
  devices: TreeDevice[];
}

// Keyword-based icon lookup — device types are free-text (admin-defined, not
// a fixed enum, per DeviceType.cs), so this matches on name/category
// substrings rather than a fixed id map. First match wins.
const ICON_RULES: Array<{ keywords: string[]; icon: React.ReactNode }> = [
  { keywords: ['trap'], icon: <PestControlIcon fontSize="small" /> },
  { keywords: ['camera', 'ptz', 'cam'], icon: <CameraAltIcon fontSize="small" /> },
  { keywords: ['battery'], icon: <BatteryChargingFullIcon fontSize="small" /> },
  { keywords: ['relay', 'stream', 'rtsp'], icon: <SettingsInputAntennaIcon fontSize="small" /> },
  { keywords: ['drone', 'mavic', 'dji'], icon: <FlightIcon fontSize="small" /> },
  { keywords: ['sensor', 'ble', 'bluetooth'], icon: <SensorsIcon fontSize="small" /> },
  { keywords: ['gateway', 'hub', 'router'], icon: <RouterIcon fontSize="small" /> },
];

const getDeviceIcon = (device: TreeDevice): React.ReactNode => {
  const haystack = `${device.deviceType?.name ?? ''} ${device.deviceType?.deviceCategory ?? ''}`.toLowerCase();
  const rule = ICON_RULES.find((r) => r.keywords.some((kw) => haystack.includes(kw)));
  return rule ? rule.icon : <DevicesOtherIcon fontSize="small" />;
};

interface TreeNode {
  device: TreeDevice;
  children: TreeNode[];
}

const buildTree = (devices: TreeDevice[]): TreeNode[] => {
  const byId = new Map<string, TreeNode>();
  devices.forEach((d) => byId.set(d.id, { device: d, children: [] }));

  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const parentNode = node.device.parentId ? byId.get(node.device.parentId) : undefined;
    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortByName = (a: TreeNode, b: TreeNode) => a.device.name.localeCompare(b.device.name);
  const sortRecursive = (nodes: TreeNode[]) => {
    nodes.sort(sortByName);
    nodes.forEach((n) => sortRecursive(n.children));
  };
  sortRecursive(roots);

  return roots;
};

const nodeMatches = (node: TreeNode, query: string): boolean => {
  const haystack = `${node.device.name} ${node.device.deviceType?.name ?? ''}`.toLowerCase();
  if (haystack.includes(query)) return true;
  return node.children.some((c) => nodeMatches(c, query));
};

const countAll = (node: TreeNode): number =>
  1 + node.children.reduce((sum, c) => sum + countAll(c), 0);

const DeviceRow: React.FC<{
  node: TreeNode;
  depth: number;
  query: string;
  navigate: ReturnType<typeof useNavigate>;
  isMobile: boolean;
}> = ({ node, depth, query, navigate, isMobile }) => {
  const [expanded, setExpanded] = useState(depth === 0 || query.length > 0);
  const hasChildren = node.children.length > 0;
  const visibleChildren = query
    ? node.children.filter((c) => nodeMatches(c, query))
    : node.children;

  React.useEffect(() => {
    if (query) setExpanded(true);
  }, [query]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          pl: 1.5 + depth * 3,
          pr: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': { borderBottom: 0 },
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          {hasChildren ? (
            <IconButton size="small" onClick={() => setExpanded((e) => !e)} sx={{ p: 0.25 }}>
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : null}
        </Box>

        <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>
          {getDeviceIcon(node.device)}
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={depth === 0 ? 600 : 400} noWrap>
            {node.device.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {node.device.deviceType?.name ?? 'Unknown type'}
          </Typography>
        </Box>

        {hasChildren && !isMobile && (
          <Chip
            label={`${countAll(node) - 1} linked`}
            size="small"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        )}

        <Chip
          label={node.device.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={node.device.isActive ? 'success' : 'default'}
          sx={{ flexShrink: 0 }}
        />

        {isMobile ? (
          <IconButton
            size="small"
            onClick={() => navigate(`/devices/${node.device.id}/events`)}
            sx={{ flexShrink: 0 }}
            title="Events"
          >
            <EventNoteIcon fontSize="small" />
          </IconButton>
        ) : (
          <Button
            size="small"
            onClick={() => navigate(`/devices/${node.device.id}/events`)}
            sx={{ flexShrink: 0 }}
          >
            Events
          </Button>
        )}
      </Box>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box>
            {visibleChildren.map((child) => (
              <DeviceRow key={child.device.id} node={child} depth={depth + 1} query={query} navigate={navigate} isMobile={isMobile} />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const DeviceTree: React.FC<DeviceTreeProps> = ({ devices }) => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const roots = useMemo(() => buildTree(devices), [devices]);

  const query = search.trim().toLowerCase();
  const visibleRoots = query ? roots.filter((r) => nodeMatches(r, query)) : roots;

  if (devices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
        No devices found
      </Typography>
    );
  }

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search devices…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {visibleRoots.length === 0 ? (
        <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
          No devices match "{search}"
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visibleRoots.map((root) => (
            <Paper key={root.device.id} variant="outlined" sx={{ overflow: 'hidden' }}>
              <DeviceRow node={root} depth={0} query={query} navigate={navigate} isMobile={isMobile} />
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DeviceTree;
